import React from 'react';
import type { User } from '../../services/users/users.service';

interface UsersTableProps {
  users: User[];
  onUserClick: (user: User) => void;
  isLoading: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onUserClick,
  isLoading
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatLastAccess = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoUsuarioColor = (tipo: string) => {
    switch (tipo) {
      case 'administrador':
        return 'bg-blue-100 text-blue-800';
      case 'miembro':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Usuario
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tipo
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Roles
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Último Acceso
            </th>
            <th className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.isArray(users) && users.map((user) => (
            <tr 
              key={user.id} 
              className="hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onUserClick(user)}
            >
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="shrink-0 h-8 w-8 md:h-10 md:w-10">
                    <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 flex items-center justify-center">
                      <span className="text-white text-xs md:text-xs font-medium">
                        {user.nombreCompleto.charAt(0).toUpperCase()}
                        {user.nombreCompleto.split(' ')[1]?.charAt(0)?.toUpperCase() || ''}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 md:ml-4">
                    <div className="text-xs md:text-xs font-medium text-gray-900">
                      {user.nombreCompleto}
                    </div>
                    {user.membresia && (
                      <div className="text-xs md:text-xs text-gray-500">
                        {user.membresia.numeroDocumento}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <div className="text-xs md:text-xs text-gray-900">{user.email}</div>
                {user.requiereCambioContrasena && (
                  <div className="text-[10px] md:text-xs text-orange-600 bg-orange-50 px-1.5 md:px-2 py-0.5 md:py-1 rounded mt-1 inline-block">
                    Requiere cambio
                  </div>
                )}
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium rounded-full ${getTipoUsuarioColor(user.tipoUsuario)}`}>
                  {user.tipoUsuario}
                </span>
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <div className="flex flex-col space-y-1">
                  {user.roles && user.roles.length > 0 ? (
                    user.roles.slice(0, 2).map((role) => (
                      <span 
                        key={role.id} 
                        className="inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium bg-gray-100 text-gray-700 rounded-md"
                      >
                        {role.nombre}
                      </span>
                    ))
                  ) : (
                    <span className="text-[10px] md:text-xs text-gray-400">Sin roles</span>
                  )}
                  {user.roles && user.roles.length > 2 && (
                    <span className="text-[10px] md:text-xs text-gray-500">+{user.roles.length - 2} más</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap text-xs md:text-xs text-gray-500">
                {formatLastAccess(user.ultimoAcceso)}
              </td>
              <td className="px-3 py-2 md:px-4 md:py-3 lg:px-6 lg:py-4 whitespace-nowrap">
                <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-[10px] md:text-xs font-medium rounded-full ${
                  user.estado 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.estado ? 'Activo' : 'Inactivo'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Empty state */}
      {Array.isArray(users) && users.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h3 className="mt-2 text-xs font-medium text-gray-900">No hay usuarios registrados</h3>
          <p className="mt-1 text-xs text-gray-500">Los usuarios registrados aparecerán aquí.</p>
        </div>
      )}
    </div>
  );
};

export default UsersTable;